import { styled } from '@mui/material/styles';
import type { ComponentPropsWithRef, ElementType } from 'react';
import { createElement, forwardRef } from 'react';

/**
 * Wraps styled() so the MUI component is resolved lazily at render time.
 *
 * Rolldown (Vite 8) wraps some MUI v9 ESM modules in __esmMin thunks due to
 * circular dependencies. When user code calls styled(Component) at module
 * top-level, the thunk may not have run yet and Component is still undefined.
 *
 * Usage: replace
 *   const Foo = styled(Button)(styles)
 * with
 *   const Foo = lazyStyled(() => Button)(styles)
 *
 * The getter `() => Button` captures the module-scope variable as a live
 * binding, so it resolves to the real component by the time the first render
 * occurs.
 */
export function lazyStyled<T extends ElementType>(getComponent: () => T) {
  const Wrapper = forwardRef<unknown, ComponentPropsWithRef<T>>((props, ref) => {
    const Component = getComponent() as ElementType;
    return createElement(Component, { ...props, ref });
  });
  Wrapper.displayName = 'LazyStyledWrapper';

  return styled(Wrapper) as ReturnType<typeof styled<T>>;
}
